import { useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useLang, translations } from "../i18n";

const MARKET_RATE = 546350; // BRL per BTC
const VILVERIN_FEE_PCT = 1.5;
const SATS_PER_BTC = 100_000_000;

function formatBrl(v: number) {
  return v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatSats(v: number) {
  return Math.round(v).toLocaleString("pt-BR");
}

export function PricingSection() {
  const { lang } = useLang();
  const t = translations.pricing;
  const ref = useScrollReveal(80);

  const [amountBrl, setAmountBrl] = useState(500);
  const [spreadPct, setSpreadPct] = useState(1.5);

  // Calculations
  const vFeeAmount = amountBrl * (VILVERIN_FEE_PCT / 100);
  const spreadAmount = amountBrl * (spreadPct / 100);
  const totalFees = vFeeAmount + spreadAmount;
  const netBrl = amountBrl - totalFees;
  const customerReceivesSats = (netBrl / MARKET_RATE) * SATS_PER_BTC;
  const revenueSats = (spreadAmount / MARKET_RATE) * SATS_PER_BTC;

  // Monthly projection (assume 1000 tx/month at this amount)
  const monthlyTx = 1000;
  const monthlyRevenueSats = revenueSats * monthlyTx;
  const monthlyRevenueBrl = spreadAmount * monthlyTx;
  const monthlyVolumeBrl = amountBrl * monthlyTx;

  return (
    <section
      id="pricing"
      className="bg-[var(--background)] py-20 md:py-28 lg:py-32 px-6 md:px-12 lg:px-20"
    >
      <div ref={ref} className="max-w-4xl mx-auto">
        <div className="text-center mb-14">
          <span className="reveal inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--primary)] px-3 py-1.5 bg-[var(--primary)]/8 border border-[var(--primary)]/15 rounded-full mb-4">
            {t.badge[lang]}
          </span>
          <h2 className="reveal text-3xl md:text-[40px] font-extrabold tracking-tight text-[var(--foreground)] leading-[1.15] mb-4">
            {t.heading[lang]}
          </h2>
          <p className="reveal text-[15px] text-[var(--muted-foreground)] max-w-lg mx-auto leading-relaxed">
            {t.description[lang]}
          </p>
        </div>

        {/* Interactive calculator */}
        <div className="reveal max-w-md mx-auto mb-12">
          <div className="bg-[var(--code-bg)] border border-[var(--border)] rounded-xl overflow-hidden shadow-xl">
            <div className="px-5 py-3 bg-black/20 border-b border-white/[0.06]">
              <span className="font-mono text-[12px] font-medium text-white/40">
                {t.rateBreakdown[lang]}
              </span>
            </div>
            <div className="p-5 space-y-4">
              {/* Transaction amount slider */}
              <div>
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-[11px] text-white/30 uppercase tracking-wider font-semibold">
                    {t.transactionAmount[lang]}
                  </span>
                  <span className="font-mono text-sm text-white/70 font-semibold">
                    R$ {formatBrl(amountBrl)}
                  </span>
                </div>
                <input
                  type="range"
                  min={50}
                  max={5000}
                  step={50}
                  value={amountBrl}
                  onChange={(e) => setAmountBrl(Number(e.target.value))}
                  className="w-full accent-[var(--primary)] h-1 bg-white/10 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--primary)]"
                />
              </div>

              {/* Spread slider */}
              <div>
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-[11px] text-[var(--primary)] uppercase tracking-wider font-semibold">
                    {t.yourSpread[lang]}
                  </span>
                  <span className="font-mono text-sm text-[var(--primary)] font-bold">
                    {spreadPct.toFixed(1)}%
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={5}
                  step={0.1}
                  value={spreadPct}
                  onChange={(e) => setSpreadPct(Number(e.target.value))}
                  className="w-full accent-[var(--primary)] h-1 bg-white/10 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--primary)]"
                />
              </div>

              {/* Rate breakdown */}
              <div className="border-t border-white/[0.06] pt-4 space-y-2.5 font-mono text-[13px]">
                <div className="flex justify-between">
                  <span className="text-white/40">{t.marketRate[lang]}</span>
                  <span className="text-white/50">R$ {formatBrl(MARKET_RATE)} / BTC</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/40">{t.vFee[lang]} ({VILVERIN_FEE_PCT}%)</span>
                  <span className="text-white/50">R$ {formatBrl(vFeeAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--primary)] font-semibold">{t.yourSpread[lang]} ({spreadPct.toFixed(1)}%)</span>
                  <span className="text-[var(--primary)] font-semibold">R$ {formatBrl(spreadAmount)}</span>
                </div>
              </div>

              {/* Results */}
              <div className="border-t border-white/[0.06] pt-4 space-y-2.5 font-mono text-[13px]">
                <div className="flex justify-between">
                  <span className="text-white/50">{t.customerRate[lang]}</span>
                  <span className="text-white/70">R$ {formatBrl(amountBrl)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">{t.customerReceives[lang]}</span>
                  <span className="text-white/70">{formatSats(customerReceivesSats)} sats</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-[var(--primary)] font-bold">{t.yourRevenue[lang]}</span>
                  <span className="text-[var(--primary)] font-bold text-base">
                    {formatSats(revenueSats)} sats
                    <span className="text-[var(--primary)]/60 text-[11px] ml-1.5 font-normal">
                      (R$ {formatBrl(spreadAmount)})
                    </span>
                  </span>
                </div>
              </div>

              {/* Monthly projection */}
              <div className="border-t border-white/[0.06] pt-4">
                <div className="p-3 rounded-lg bg-white/[0.03]">
                  <p className="text-[12px] text-white/30 mb-1">
                    {t.projection[lang].replace("{volume}", `R$ ${formatBrl(monthlyVolumeBrl)}`)}
                  </p>
                  <p className="font-mono text-sm">
                    <span className="text-white/40">{t.monthlyRevenue[lang]}:</span>{" "}
                    <span className="text-[var(--primary)] font-bold">
                      {formatSats(monthlyRevenueSats)} sats
                    </span>
                    <span className="text-white/30 text-[11px] ml-1.5">
                      (R$ {formatBrl(monthlyRevenueBrl)})
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="reveal grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {t.cards.map((card, i) => (
            <div key={i} className="p-5 rounded-xl border border-[var(--border)] bg-[var(--card)]">
              <div className="text-sm font-bold text-[var(--foreground)] mb-1">
                {card.title[lang]}
              </div>
              <div className="text-[13px] text-[var(--muted-foreground)]">
                {card.description[lang]}
              </div>
            </div>
          ))}
        </div>

        <p className="reveal text-sm text-[var(--muted-foreground)] text-center">
          {t.footer[lang]}{" "}
          <a href="#waitlist" className="text-[var(--primary)] font-semibold hover:underline">
            {t.footerCta[lang]}
          </a>{" "}
          {lang === "en" ? "for details." : "para mais detalhes."}
        </p>
      </div>
    </section>
  );
}
